import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort, hours, pct } from "../format.js";
import {
  Async, Badge, Bar, Button, Card, Empty, ErrorPanel, Field, Fields, Grid, Input, Modal, Ribbon,
  Select, Sep, Table, Tabs, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const termsLabel = (days) => (Number(days) ? `Net ${Number(days)} days` : "Due on receipt");

export default function ClientDetail({ id }) {
  const { settings, reloadApp, me } = useApp();
  const canManage = !!me.permissions["clients.manage"];
  const canInvoice = !!me.permissions["invoices.manage"];
  const state = useAsync(() => api.get(`clients/${id}`), [id]);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const sym = settings.currency_symbol;

  return (
    <>
      <TopBar crumb="Clients" title={state.data?.client?.name || "Client"}>
        {canManage && (
          <Button onClick={() => setEditing(true)} disabled={!state.data}>
            Edit
          </Button>
        )}
        {canInvoice && (
          <Button kind="pri" onClick={() => navigate("/invoices/new")}>
            ＋ New invoice
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(raw) => {
            const d = {
              ...raw,
              invoices: raw.invoices || [],
              projects: raw.projects || [],
              estimates: raw.estimates || [],
              time: raw.time || [],
            };
            return (
              <>
                <Tabs
                  value={tab}
                  onChange={setTab}
                  tabs={[
                    { key: "overview", label: "Overview" },
                    { key: "projects", label: `Projects${d.projects.length ? ` (${d.projects.length})` : ""}` },
                    { key: "estimates", label: `Estimates${d.estimates.length ? ` (${d.estimates.length})` : ""}` },
                    { key: "time", label: "Time" },
                  ]}
                />

                {tab === "overview" && (
                  <Overview d={d} sym={sym} onEdit={canManage ? () => setEditing(true) : null} canInvoice={canInvoice} />
                )}
                {tab === "projects" && <Projects rows={d.projects} sym={sym} />}
                {tab === "estimates" && <Estimates rows={d.estimates} sym={sym} />}
                {tab === "time" && <TimeTab rows={d.time} />}
              </>
            );
          }}
        </Async>
      </Wrap>

      {editing && state.data && (
        <EditClientModal
          client={state.data.client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            state.reload();
            reloadApp();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- overview */

function Overview({ d, sym, onEdit, canInvoice }) {
  const { client, stats, invoices } = d;
  const avg = stats?.avg_days_to_pay == null ? null : Number(stats.avg_days_to_pay);
  const late = avg !== null && avg > 45;

  return (
    <>
      <Grid cols="1fr 1fr" style={{ alignItems: "start" }}>
        <Card title="Contact" right={onEdit ? <Button sm kind="gho" onClick={onEdit}>Edit</Button> : null}>
          <div className="sm" style={{ display: "grid", gap: 9 }}>
            <Line label="Contact" value={client.contact_name || "—"} />
            <Line label="Email" value={client.email || "—"} />
            <Line label="Phone" value={client.phone || "—"} />
            <div className="row" style={{ alignItems: "flex-start" }}>
              <span className="muted" style={{ width: 88, flex: "none" }}>
                Address
              </span>
              <span style={{ whiteSpace: "pre-line" }}>{client.address || "—"}</span>
            </div>
            <Line label="Terms" value={termsLabel(client.terms_days)} />
            <Line label="Status" value={<Badge status={client.status} />} />
            {client.notes && (
              <>
                <Sep />
                <div className="muted tiny">Internal note</div>
                <div style={{ whiteSpace: "pre-line" }}>{client.notes}</div>
              </>
            )}
          </div>
        </Card>

        <Card title="Account health">
          <div style={{ display: "grid", gap: 14 }}>
            <div className="row">
              <div>
                <div className="tiny muted">Outstanding</div>
                <div className="num" style={{ fontSize: 20, fontWeight: 700 }}>
                  {money(stats?.outstanding, sym)}
                </div>
              </div>
              <div className="grow" />
              <div className="right">
                <div className="tiny muted">Lifetime paid</div>
                <div className="num" style={{ fontSize: 20, fontWeight: 700 }}>
                  {money(stats?.lifetime_paid, sym)}
                </div>
              </div>
            </div>

            <div>
              <div className="row tiny muted">
                <span>Avg days to pay</span>
                <div className="grow" />
                <span>{avg === null ? "No paid invoices yet" : `${avg} days`}</span>
              </div>
              <div style={{ marginTop: 5 }}>
                {/* 60 days is the "very slow" end of the scale. */}
                <Bar pct={avg === null ? 0 : Math.min(100, (avg / 60) * 100)} warn={late} />
              </div>
            </div>

            <Ribbon tone={late ? "warn" : undefined}>
              {avg === null
                ? "Not enough payment history to judge yet."
                : late
                ? "⚠ Pays late consistently — consider requiring a deposit."
                : "✓ Payment history is healthy."}
            </Ribbon>
            <div className="tiny muted">
              {Number(stats?.invoice_count || 0)} invoice{Number(stats?.invoice_count || 0) === 1 ? "" : "s"} on file
            </div>
          </div>
        </Card>
      </Grid>

      <Card
        className="mt"
        bare
        title="Invoices"
        right={
          canInvoice ? (
            <Button sm kind="pri" onClick={() => navigate("/invoices/new")}>
              ＋ New invoice
            </Button>
          ) : null
        }
      >
        <Table
          rows={invoices}
          onRowClick={(i) => navigate(`/invoices/${i.id}`)}
          empty={<Empty icon="▤">No invoices yet</Empty>}
          columns={[
            { key: "number", label: "Invoice", render: (i) => <b>{i.number}</b> },
            { key: "issued", label: "Issued", render: (i) => <span className="sm dim">{fmtDateShort(i.issue_date)}</span> },
            { key: "due", label: "Due", render: (i) => <span className="sm dim">{fmtDateShort(i.due_date)}</span> },
            { key: "total", label: "Total", align: "right", render: (i) => <span className="num">{money(i.total, sym)}</span> },
            {
              key: "balance",
              label: "Balance",
              align: "right",
              render: (i) =>
                Number(i.balance || 0) ? (
                  <span className="num">{money(i.balance, sym)}</span>
                ) : (
                  <span className="muted">—</span>
                ),
            },
            { key: "status", label: "Status", render: (i) => <Badge status={i.display_status} /> },
          ]}
        />
      </Card>
    </>
  );
}

const Line = ({ label, value }) => (
  <div className="row">
    <span className="muted" style={{ width: 88, flex: "none" }}>
      {label}
    </span>
    <span>{value}</span>
  </div>
);

/* -------------------------------------------------------------- projects */

function Projects({ rows, sym }) {
  const usedPct = (p) =>
    p.billing_type === "hourly" ? pct(p.hours, p.budget_hours) : pct(p.billed, p.budget_amount);

  return (
    <Card bare>
      <Table
        rows={rows}
        onRowClick={(p) => navigate(`/projects/${p.id}`)}
        empty={<Empty icon="❏">No projects</Empty>}
        columns={[
          { key: "name", label: "Project", render: (p) => <b>{p.name}</b> },
          {
            key: "billing",
            label: "Billing",
            render: (p) => (
              <span className="sm dim">
                {p.billing_type === "hourly" ? `${money(p.hourly_rate, sym)}/hr` : `Flat ${money(p.budget_amount, sym)}`}
              </span>
            ),
          },
          {
            key: "hours",
            label: "Hours",
            align: "right",
            render: (p) => <span className="num">{Number(p.hours || 0).toFixed(1)}</span>,
          },
          {
            key: "used",
            label: "Budget used",
            align: "right",
            width: 150,
            render: (p) => {
              const v = Math.min(100, usedPct(p));
              return (
                <div className="row">
                  <div style={{ flex: 1 }}>
                    <Bar pct={v} warn={v > 85} />
                  </div>
                  <span className="tiny num" style={{ width: 34, textAlign: "right" }}>
                    {v}%
                  </span>
                </div>
              );
            },
          },
          { key: "due", label: "Due", render: (p) => <span className="sm dim">{fmtDateShort(p.due_on)}</span> },
          { key: "status", label: "Status", render: (p) => <Badge status={p.status} /> },
        ]}
      />
    </Card>
  );
}

/* -------------------------------------------------------------- estimates */

function Estimates({ rows, sym }) {
  return (
    <Card bare>
      <Table
        rows={rows}
        onRowClick={(e) => navigate(`/estimates/${e.id}`)}
        empty={<Empty icon="▢">No estimates</Empty>}
        columns={[
          { key: "number", label: "Estimate", render: (e) => <b>{e.number}</b> },
          { key: "title", label: "Title", render: (e) => e.title || <span className="muted">—</span> },
          { key: "issued", label: "Issued", render: (e) => <span className="sm dim">{fmtDateShort(e.issue_date)}</span> },
          { key: "expires", label: "Expires", render: (e) => <span className="sm dim">{fmtDateShort(e.expires_on)}</span> },
          { key: "total", label: "Total", align: "right", render: (e) => <span className="num">{money(e.total, sym)}</span> },
          { key: "status", label: "Status", render: (e) => <Badge status={e.status} /> },
        ]}
      />
    </Card>
  );
}

/* -------------------------------------------------------------- time */

function TimeTab({ rows }) {
  return (
    <Card bare>
      <Table
        rows={rows}
        empty={<Empty icon="◷">No time logged</Empty>}
        columns={[
          { key: "date", label: "Date", render: (t) => <span className="sm dim">{fmtDateShort(t.entry_date)}</span> },
          { key: "who", label: "Who", render: (t) => <span className="sm">{t.user_name}</span> },
          { key: "project", label: "Project", render: (t) => <span className="sm">{t.project_name || "—"}</span> },
          { key: "service", label: "Service", render: (t) => <span className="sm dim">{t.item_name || "—"}</span> },
          { key: "note", label: "Note", render: (t) => <span className="sm dim">{t.note}</span> },
          { key: "hours", label: "Hours", align: "right", render: (t) => <span className="num">{hours(t.minutes)}</span> },
          {
            key: "billed",
            label: "Billed",
            render: (t) =>
              t.invoice_id ? (
                <Badge status="billed" />
              ) : t.billable ? (
                <Badge status="unbilled" />
              ) : (
                <Badge status="internal" label="Non-billable" />
              ),
          },
        ]}
      />
    </Card>
  );
}

/* -------------------------------------------------------------- edit modal */

function EditClientModal({ client, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: client.name || "",
    contact_name: client.contact_name || "",
    email: client.email || "",
    phone: client.phone || "",
    address: client.address || "",
    terms_days: client.terms_days ?? 30,
    notes: client.notes || "",
    status: client.status || "active",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function save() {
    setError(null);
    setBusy(true);
    try {
      await api.put(`clients/${client.id}`, { ...form, terms_days: Number(form.terms_days) });
      toast.push("Client updated");
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Edit client"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Save changes
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Business name">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Primary contact">
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </Field>
        </Fields>
        <Fields cols={2}>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
        </Fields>
        <Field label="Billing address">
          <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
        </Field>
        <Fields cols={2}>
          <Field label="Payment terms">
            <Select value={form.terms_days} onChange={(e) => set("terms_days", e.target.value)}>
              <option value={15}>Net 15</option>
              <option value={30}>Net 30</option>
              <option value={45}>Net 45</option>
              <option value={0}>Due on receipt</option>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </Select>
          </Field>
        </Fields>
        <Field label="Internal note">
          <Textarea
            rows={2}
            value={form.notes}
            placeholder="Only your team sees this"
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </Fields>
    </Modal>
  );
}
